// Colorado School of Mines: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Mines' newest posted Common Data Set is 2022-2023 (Institutional
//    Research, Common Data Set page; a search of mines.edu found no newer
//    edition), three cycles old, so it is not used. The first-year counts
//    come from the U.S. Department of Education's IPEDS Admissions survey
//    for Fall 2024, first-time degree-seeking students (College Navigator
//    and the ADM2024 complete data file, unit 126775). IPEDS gives no
//    residency split, so this page has none.
//  - mines.edu Undergraduate Admissions: First-Year Admission Requirements
//    (application dates for fall 2027 admission, test policy, Golden
//    Guarantee) and First-Year Frequently Asked Questions (Early Action, not
//    binding).
//  - Mines Financial Aid, Undergraduate Cost of Attendance: the 2026-2027
//    on-campus budgets for Colorado residents and non-residents.
//  - College Scorecard API, school id 126775 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=126775#admsns',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Mines Undergraduate Admissions, First-Year Admission Requirements',
        url: 'https://www.mines.edu/undergraduate-admissions/first-year/',
        read: '2026-10-01',
    },
    faq: {
        label: 'Mines Undergraduate Admissions, First-Year Frequently Asked Questions',
        url: 'https://www.mines.edu/undergraduate-admissions/frequently-asked-questions/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Mines Financial Aid, Undergraduate Cost of Attendance',
        url: 'https://finaid.mines.edu/cost-of-attendance-undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?126775-Colorado-School-of-Mines',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'colorado-school-of-mines',
    kbId: 'colorado_school_of_mines',
    name: 'Colorado School of Mines',
    shortName: 'Mines',
    aliases: ['Mines'],
    location: 'Golden, CO',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Mines brand guidelines, colors, fonts and graphic elements); decoration only.
    branding: {
        primary: '#21314D',
        secondary: '#879EC3',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/colorado_school_of_mines_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 11436,
        admitted: 6940,
        enrolled: 1602,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Mines has not posted a Common Data Set since 2022-2023, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole school and none by major.',
        source: 'ipeds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular deadline', value: 'January 15' },
        rows: [
            { plan: 'Early Action (priority deadline)', deadline: 'November 1', decision: 'By December 15', binding: false },
            { plan: 'Regular', deadline: 'January 15', decision: 'By February 15', binding: false },
        ],
        note: 'Applications open August 1, through the Common App or Mines\' Golden App, with no preference for either. To be considered early, apply by November 1 and select Early Action on the application. After January 15, applications are reviewed on a rolling basis as space allows. Colorado high school students can apply for free October 20 to 22, 2026. Colorado residents graduating from a Colorado high school who meet the Mines Golden Guarantee requirements and apply by November 1 are guaranteed admission. Admitted students submit the enrollment decision form and deposit by May 1.',
        source: 'firstYear',
        binding: 'Admission to Mines is not binding: if you are admitted, early or regular, you have until May 1 to accept or decline.',
        bindingSource: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Mines is test-optional: your application gets equal consideration for admission and merit scholarships with or without SAT or ACT scores. If you send scores, Mines superscores both tests.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [19800, 44790] },
            { item: 'Fees', amounts: [3206, 3206] },
            { item: 'Housing and food (on campus)', amounts: [18900, 18900] },
            { item: 'Books, materials, equipment and supplies', amounts: [1500, 1500] },
            { item: 'Personal expenses', amounts: [1618, 1618] },
            { item: 'Transportation', amounts: [1400, 1400] },
            { item: 'Loan fees', amounts: [56, 56] },
        ],
        totals: [46480, 71470],
        note: 'For full-time students living on campus. In-state tuition is for Colorado residents and already takes the College Opportunity Fund stipend into account. The student health insurance plan is not included.',
        source: 'coa',
        netPrice: {
            average: 32901,
            byIncome: [
                { income: '$0 to $30,000', amount: 21504 },
                { income: '$30,001 to $48,000', amount: 22896 },
                { income: '$48,001 to $75,000', amount: 23913 },
                { income: '$75,001 to $110,000', amount: 28115 },
                { income: 'Over $110,000', amount: 38429 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Mines\' aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
